import React, {Fragment, FunctionComponent, ReactNode} from "react"
import {IconButton} from "@App/Components/common/IconButton/IconButton"
import {IconButtonSkeleton} from "@App/Components/common/IconButton/IconButtonSkeleton"
import {Hero} from "@App/Components/Hero"
import {LocationFilter} from "@App/Components/Filters"
import {ContactUs} from "@App/Components/ContactUs"
import {TriangleDivider} from "@App/Components/TriangleDivider"
import {
    BonusBenefitsIcon,
    CalendarCheckBenefitsIcon,
    CheckBenefitsIcon,
    GlassIcon,
    InspectionIcon,
    MiscIcon,
    WheelchangeIcon
} from "@App/Components/common/Icons"
import {useDispatch, useSelector} from "react-redux";
import {actionCreators, State} from '@App/state'
import {bindActionCreators} from "@reduxjs/toolkit";
import {Modal} from "@App/Components/Modal";
import {Service} from "@App/types";
import {Stepper} from "@App/Components/Stepper/Stepper";
import {useServices} from "@App/hooks/api/useServices";
import {BulletPoint} from "@App/Components/common/BulletPoint";
import {useTranslation} from "react-i18next";
import {useHome} from "@App/hooks/useHome";

export type HomeProps = {
    embedded?: boolean
    onSubmit: () => void
}

export const Home: FunctionComponent<HomeProps> = ({embedded = false, onSubmit}: HomeProps) => {

    const dispatch = useDispatch();
    const {chooseService} = bindActionCreators(actionCreators, dispatch)
    const {t} = useTranslation()

    const organization = useSelector((state: State) => state.orders.organization)
    const service = useSelector((state: State) => state.orders.service)
    const place = useSelector((state: State) => state.orders.place)

    const {loading, error, services} = useServices()

    const {
        currentStep,
        setCurrentStep
    } = useHome()

    const selectService = (selectedService: Service): void => {
        chooseService(selectedService)
        setCurrentStep(currentStep + 1)

        if (typeof ga !== 'undefined') {
            ga('send', 'event', organization.name, selectedService.name, '');
        }
    }

    const submit = () => {
        onSubmit && onSubmit()
    }

    const getIcon = (name: string): ReactNode => {
        let icons = {
            'Inspection': <InspectionIcon/>,
            'Wheelchange': <WheelchangeIcon/>,
            'Glas': <GlassIcon/>,
            'Misc': <MiscIcon/>
        }

        return icons[name]
    }

    if (error) {
        console.log(error)
    }

    return (
        <Fragment>
            <Hero
                title={t("hero.title", {organization: organization.name})}
                subtitle={t("hero.subtitle", {organization: organization.name})}
                backgroundImage={organization.settings.hero.background_image_url ?? '/images/brand/arval-banner-darkened.jpg'}
            />
            <Modal open={organization.settings.welcome_modal.display ?? true} onClick={() => null}
                   title={t('welcomeModal.title', {organization: organization.name})}
                   buttonText={'Weiter'} width="3xl">
                <div>
                    {t('welcomeModal.text', {organization: organization.name})}
                </div>
            </Modal>
            <div className="bg-white px-5 pt-5 container">
                <Stepper currentStep={currentStep} onChange={setCurrentStep}>
                    <Stepper.Step
                        title={t("Waehlen Sie einen Service.")}
                        subtitle={service && service.pivot.subtitle ? service.pivot.subtitle : null}
                        completedTitle={'Service: '}
                        completedGoBackLinkTitle={service ? (service.pivot.display_name ? service.pivot.display_name : service.name) : ''}>
                        <Fragment>
                            {loading && (
                                <div className="overflow-hidden">
                                    <div
                                        className={`md:w-fit ease-in-out grid grid-cols-1 xs:grid-cols-2 gap-2 mb-4 mt-4 w-full`}>
                                        <IconButtonSkeleton/>
                                        <IconButtonSkeleton/>
                                        <IconButtonSkeleton/>
                                        <IconButtonSkeleton/>
                                    </div>
                                </div>
                            )}
                            {error && (
                                <div>
                                    {t('error')}
                                </div>
                            )}
                            {!loading && services && (
                                <div className="overflow-hidden">
                                    <div
                                        className={`md:w-fit ease-in-out grid grid-cols-1 xs:grid-cols-2 gap-2 mb-4 mt-4 w-full`}>
                                        {services.map((service) =>
                                            <IconButton
                                                key={service.id}
                                                title={service.pivot.display_name ? service.pivot.display_name : service.name}
                                                onClick={() => selectService(service)}
                                                subtitle={service.pivot.subtitle}
                                                icon={getIcon(service.pivot.icon)}/>
                                        )}
                                    </div>
                                </div>
                            )}
                        </Fragment>
                    </Stepper.Step>
                    <Stepper.Step title={t("Waehlen Sie Ihren Wunschort.")}
                                  completedTitle={''}
                                  completedGoBackLinkTitle={service ? service.name : ''}>
                        <Fragment>
                            <div className={`pb-32 md:pb-20`}>
                                <div className={`md:w-80`}>
                                    <LocationFilter className="relative" onSubmit={submit}/>
                                </div>
                                <div
                                    onClick={submit}
                                    className={`${!service || (!place || !place.lat) ? 'cursor-pointer pointer-events-none no-underline' : ''}`}>
                                    <div
                                        className={`${!service || !place ? 'bg-primary-100' : 'bg-primary'} w-full md:w-80 px-8 py-2 mt-4 text-white rounded text-center font-serif font-bold cursor-pointer no-underline`}>
                                        {t('Werkstatt suchen')}
                                    </div>
                                </div>
                            </div>
                        </Fragment>
                    </Stepper.Step>
                </Stepper>
            </div>
            {!embedded && (
                <div className="bg-neutral-100">
                    <TriangleDivider direction="left" background={"bg-white"}/>
                    <div className="px-5 py-5 container md:pb-20">
                        <div className="md:grid md:grid-cols-[50%_50%]">
                            <div className="md:pr-12">
                                <div className="font-serif text-2xl mb-4">{t('benefits.title')}</div>
                                <BulletPoint text={t('benefits.bullet-point-1', {organization: organization.name})}>
                                    <CalendarCheckBenefitsIcon/>
                                </BulletPoint>
                                <BulletPoint
                                    text={t('benefits.bullet-point-2', {organization: organization.name})}>
                                    <CheckBenefitsIcon/>
                                </BulletPoint>
                                <BulletPoint
                                    text={t('benefits.bullet-point-3', {organization: organization.name})}>
                                    <BonusBenefitsIcon/>
                                </BulletPoint>
                            </div>
                            <div className="pt-5 md:pt-0 md:ml-12">
                                <ContactUs/>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}
